import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  getBox,
  searchInventoryProducts,
  InventoryApiError,
  type InventoryBox,
  type InventoryProduct,
} from "@/lib/inventory/api";
import { useInventoryText } from "@/lib/inventory/i18n";
import {
  validBoxCode,
  useIntakeValidationText,
  type SerialIssue,
} from "@/lib/inventory/intakeValidation";
import { BarcodeCamera } from "./BarcodeCamera";
import { InventoryBoxActions } from "./InventoryBoxActions";
import { InventoryAudit } from "./InventoryViews";
import {
  InventoryReceive,
  serialLines,
  serialProblem,
} from "./InventoryReceive";
import {
  ErrorNotice,
  Field,
  Loading,
  Panel,
  conditionText,
  selectClass,
  statusText,
  useInventoryAccess,
} from "./shared";

function canCapture(box: InventoryBox): boolean {
  const pending = ["QUARANTINE_PENDING_POLICY", "QUARANTINE_PENDING_SERIALS"];
  return (
    box.status !== "VOIDED" &&
    (box.status === "UNASSIGNED" ||
      pending.includes(box.condition) ||
      pending.includes(box.status) ||
      (box.status === "ASSIGNED" &&
        ["QUARANTINED", "QUARANTINE"].includes(box.condition) &&
        box.serial_policy !== "NONE" &&
        (box.quantity ?? 0) > (box.serials?.length ?? 0)))
  );
}
async function intakeProduct(
  box: InventoryBox,
): Promise<InventoryProduct | null> {
  if (box.status === "UNASSIGNED" || !canCapture(box)) return null;
  const result = await searchInventoryProducts(String(box.product_id));
  const product = result.products.find((p) => p.product_id === box.product_id);
  if (!product) throw new Error("Intake product metadata unavailable");
  return product;
}
export function InventoryScan({ initialCode = "" }: { initialCode?: string }) {
  const t = useInventoryText();
  const validationText = useIntakeValidationText();
  const access = useInventoryAccess();
  const [mode, setMode] = useState("BOX");
  const [code, setCode] = useState(initialCode);
  const [box, setBox] = useState<InventoryBox | null>(null);
  const [product, setProduct] = useState<InventoryProduct | null>(null);
  const [serialText, setSerialText] = useState("");
  const [busy, setBusy] = useState(false);
  const [receiveLocked, setReceiveLocked] = useState(false);
  const [actionLocked, setActionLocked] = useState(false);
  const locked = receiveLocked || actionLocked;
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<
    "duplicateScan" | SerialIssue | "invalidBox" | "chooseBox" | null
  >(null);
  const input = useRef<HTMLInputElement>(null);
  const request = useRef(0);
  const lookupLock = useRef(false);
  useEffect(() => {
    if (!busy && !locked) input.current?.focus();
  }, [busy, locked]);
  useEffect(() => {
    input.current?.focus();
    return () => {
      request.current += 1;
    };
  }, []);
  async function lookup(value: string) {
    if (lookupLock.current || locked) return;
    if (!validBoxCode(value)) {
      setError(null);
      setNotice("invalidBox");
      return;
    }
    lookupLock.current = true;
    const current = ++request.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await getBox(value);
      const metadata = await intakeProduct(result);
      if (current !== request.current) return;
      if (box?.box_id === result.box_id) setNotice("duplicateScan");
      else setSerialText("");
      setProduct(metadata ?? (box?.box_id === result.box_id ? product : null));
      setBox(result);
    } catch (e) {
      if (current === request.current) {
        setError(e);
        setBox(null);
        setProduct(null);
        setSerialText("");
      }
    } finally {
      if (current === request.current) {
        setBusy(false);
        lookupLock.current = false;
      }
    }
  }
  useEffect(() => {
    if (initialCode) void lookup(initialCode);
  }, [initialCode]);
  function capture(value: string) {
    if (locked || busy) return;
    setNotice(null);
    if (mode === "BOX") {
      if (!validBoxCode(value)) {
        setNotice("invalidBox");
        return;
      }
      setCode(value);
      void lookup(value);
      return;
    }
    if (!box || (!product && box.status === "UNASSIGNED") || !canCapture(box)) {
      setNotice("chooseBox");
      return;
    }
    if (!access.write) return;
    const lines = serialLines(serialText);
    const problem = serialProblem(
      [
        ...(box.status !== "UNASSIGNED" ? box.serials || [] : []),
        ...lines,
        value,
      ],
      box.box_id,
      product?.ean,
    );
    if (problem) {
      setNotice(problem);
      return;
    }
    if (product?.serial_policy === "NONE") {
      setNotice("chooseBox");
      return;
    }
    // Text is preserved; normalized identity is used for comparison only.
    setSerialText([...lines, value].join("\n"));
    setCode("");
    input.current?.focus();
  }
  async function refreshBox() {
    if (!box) return;
    const latest = await getBox(box.box_id);
    const metadata = await intakeProduct(latest);
    setBox(latest);
    setProduct(metadata);
    setSerialText("");
  }
  const receiving = box && canCapture(box);
  return (
    <div className="space-y-4">
      <Panel title={t.scan}>
        <p>{mode === "BOX" ? t.scanHelp : t.unitHelp}</p>
        <Field label={t.scan}>
          <select
            className={selectClass}
            value={mode}
            disabled={busy || locked}
            onChange={(e) => {
              setMode(e.target.value);
              setCode("");
              setNotice(null);
              input.current?.focus();
            }}
          >
            <option value="BOX">{t.boxMode}</option>
            <option value="UNIT">{t.unitMode}</option>
          </select>
        </Field>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t.code}>
            <Input
              ref={input}
              value={code}
              autoComplete="off"
              maxLength={mode === "BOX" ? 40 : 162}
              disabled={busy || locked}
              onChange={(e) => setCode(e.target.value)}
              onPaste={(e) => {
                // Validate the raw paste before the single-line input or its
                // maxlength can silently remove controls/truncate an identity.
                const field = e.currentTarget;
                const value =
                  code.slice(0, field.selectionStart ?? code.length) +
                  e.clipboardData.getData("text") +
                  code.slice(field.selectionEnd ?? code.length);
                const issue =
                  mode === "BOX"
                    ? validBoxCode(value)
                      ? null
                      : "invalidBox"
                    : serialProblem([value], box?.box_id ?? "", product?.ean);
                if (issue) {
                  e.preventDefault();
                  setNotice(issue);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  capture(code);
                }
              }}
            />
          </Field>
          <Button
            disabled={busy || locked || !code.trim()}
            onClick={() => capture(code)}
          >
            {t.lookup}
          </Button>
        </div>
        <BarcodeCamera onCode={capture} disabled={busy || locked} />
        {busy && <Loading />}
        {notice && (
          <p role="alert">
            {notice === "invalidSerial" || notice === "invalidBox"
              ? validationText[notice]
              : t[notice]}
          </p>
        )}
        {error instanceof InventoryApiError && error.status === 404 ? (
          <p role="alert">{t.unknownBox}</p>
        ) : (
          <ErrorNotice error={error} />
        )}
        {box && (
          <div className="space-y-2">
            <code>{box.box_id}</code>
            <p>
              {t.status}: {statusText(t, box.status)}{" "}
              <span className="font-mono text-xs">({box.status})</span>
            </p>
            <p>
              {t.version}: {box.row_version}
            </p>
            <p>
              {t.condition}: {conditionText(t, box.condition)}{" "}
              <code>({box.condition})</code>
            </p>
            <p>
              {t.quantity}: {box.quantity ?? "—"} · {t.location}:{" "}
              {box.location_name ?? "—"}
            </p>
            {!receiving && box.serials?.length > 0 && (
              <div>
                <h3>{t.recordedSerials}</h3>
                <ul>
                  {box.serials.map((s, i) => (
                    <li key={i} className="whitespace-pre-wrap font-mono">
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {box.status === "VOIDED" && <p role="alert">{t.voided}</p>}
            {box.status !== "UNASSIGNED" && box.status !== "VOIDED" && (
              <p>{t.alreadyReceived}</p>
            )}
          </div>
        )}
      </Panel>
      {receiving && (
        <fieldset disabled={actionLocked}>
          <InventoryReceive
            key={`receive-${box.box_id}-${box.row_version}`}
            box={box}
            product={product}
            onProduct={setProduct}
            serialText={serialText}
            onSerialText={setSerialText}
            onSaved={refreshBox}
            onBusy={setReceiveLocked}
          />
        </fieldset>
      )}
      {box && (
        <fieldset disabled={receiveLocked}>
          <InventoryBoxActions
            box={box}
            onSaved={refreshBox}
            onBusy={setActionLocked}
          />
        </fieldset>
      )}
      {box && (
        <InventoryAudit
          key={`${box.box_id}-${box.row_version}`}
          boxId={box.box_id}
        />
      )}
    </div>
  );
}
